import { useMemo, useState } from "react";
import { MessageSquareShare, UserPen } from "lucide-react";
import {
  FOGBUGZ_TO_ADO_REPORTER_QUESTION_MAX,
  FOGBUGZ_TO_ADO_SLOT_LABELS,
  FOGBUGZ_TO_ADO_SLOT_VALUE_MAX,
  composeReporterQuestionsNote,
  type FogbugzBugBarResult,
  type FogbugzToAdoBugSlotId,
  type FogbugzToAdoPmAddition,
  type FogbugzToAdoReporterQuestion,
  type FogbugzToAdoSlotVerdict,
} from "@ttc-spectree/toolbox-contract";
import { Button } from "@/components/ui/button";
import { useFogbugzToAdoBarCheck, usePostReporterQuestions } from "@/hooks/queries/use-fogbugz-to-ado";
import { applyPmAnswers, draftReporterQuestions, missingSlots } from "@/lib/toolbox/fogbugz-to-ado";
import { cn } from "@/lib/utils";
import { BarResult } from "./bar-step";
import { Notice, errorText } from "./parts";

/**
 * Below the bar (epic PI-SPEC-SW-E311, task PI-SPEC-SW-1640-3, Resolved
 * Decision 7): the PM chooses each time, nothing preselected.
 *
 * - (a) "Ask the reporter": the drafted questions (Claude's for a legacy
 *   case, one per missing rule for an `intent.md` case), editable, with the
 *   exact FogBugz note as a preview. Only "Post to FogBugz case N" posts it;
 *   nothing is written to ADO.
 * - (b) "I know the answers": one box per missing detail with the drafted
 *   question as its prompt. The answers become PM additions and SpecTree
 *   checks the bar again; drafting starts only when it passes. The PM can
 *   switch to (a) at any time.
 */

type Choice = "ask" | "answer" | null;

const fieldClass = cn(
  "w-full rounded-md border border-[#3F3F46] bg-[#111113] px-3 py-2 text-[13px] leading-relaxed outline-none",
  "focus:border-[#66A1FF] focus:ring-2 focus:ring-[#66A1FF]/25",
);

export interface BelowBarStepProps {
  caseId: number;
  pmName: string;
  /** The bar as last checked (not passing). */
  bar: FogbugzBugBarResult;
  slots: readonly FogbugzToAdoSlotVerdict[];
  pmAdditions: readonly FogbugzToAdoPmAddition[];
  /** Claude's questions from the slots run; empty for an `intent.md` case. */
  claudeQuestions: readonly FogbugzToAdoReporterQuestion[];
  /** (b) passed the bar: draft with these slots and additions. */
  onPassed: (confirmed: { slots: FogbugzToAdoSlotVerdict[]; pmAdditions: FogbugzToAdoPmAddition[] }) => void;
  /** (a) posted: the questions as posted. */
  onPosted: (questions: FogbugzToAdoReporterQuestion[]) => void;
}

export function BelowBarStep({
  caseId,
  pmName,
  bar: initialBar,
  slots: initialSlots,
  pmAdditions: initialAdditions,
  claudeQuestions,
  onPassed,
  onPosted,
}: BelowBarStepProps) {
  const [choice, setChoice] = useState<Choice>(null);
  const [bar, setBar] = useState(initialBar);
  const [slots, setSlots] = useState<FogbugzToAdoSlotVerdict[]>(() => [...initialSlots]);
  const [pmAdditions, setPmAdditions] = useState<FogbugzToAdoPmAddition[]>(() => [...initialAdditions]);
  const [questions, setQuestions] = useState<FogbugzToAdoReporterQuestion[]>(() =>
    draftReporterQuestions(initialBar.missing, claudeQuestions),
  );
  const [answers, setAnswers] = useState<Partial<Record<FogbugzToAdoBugSlotId, string>>>(() => {
    // A detail found in the case that misses a rule (steps not numbered, say) starts from its value.
    const start: Partial<Record<FogbugzToAdoBugSlotId, string>> = {};
    for (const slot of missingSlots(initialBar.missing)) {
      const found = initialSlots.find((verdict) => verdict.slot === slot && verdict.status === "found")?.value;
      const added = initialAdditions.find((addition) => addition.slot === slot)?.text;
      start[slot] = added ?? found ?? "";
    }
    return start;
  });
  const post = usePostReporterQuestions();
  const recheck = useFogbugzToAdoBarCheck();

  const askSlots = missingSlots(bar.missing);
  const prompts = useMemo(() => draftReporterQuestions(bar.missing, claudeQuestions), [bar, claudeQuestions]);
  const postable = questions.filter((question) => question.question.trim() !== "");
  const preview = composeReporterQuestionsNote({
    pmName,
    missing: postable.map((question) => question.slot),
    questions: postable,
  });

  const postQuestions = () => {
    if (postable.length === 0 || post.isPending) return;
    const reviewed = postable.map((question) => ({ slot: question.slot, question: question.question.trim() }));
    post.mutate(
      { caseId, questions: reviewed },
      {
        onSuccess: () => {
          onPosted(reviewed);
        },
      },
    );
  };

  const checkAgain = () => {
    if (recheck.isPending) return;
    const next = applyPmAnswers(slots, pmAdditions, answers);
    recheck.mutate(
      { caseId, body: next },
      {
        onSuccess: (result) => {
          setSlots(next.slots);
          setPmAdditions(next.pmAdditions);
          if (result.passes) {
            onPassed(next);
            return;
          }
          setBar(result);
          setQuestions(draftReporterQuestions(result.missing, claudeQuestions));
          setAnswers((current) => {
            const kept: Partial<Record<FogbugzToAdoBugSlotId, string>> = {};
            for (const slot of missingSlots(result.missing)) kept[slot] = current[slot] ?? "";
            return kept;
          });
        },
      },
    );
  };

  return (
    <section className="flex flex-col gap-3" aria-labelledby="fogbugz-to-ado-below-bar" data-testid="fogbugz-to-ado-below-bar">
      <h2 id="fogbugz-to-ado-below-bar" className="font-display text-base font-semibold">
        Case #{caseId} is missing details a Bug needs
      </h2>
      <BarResult bar={bar} />
      <p className="text-[13px] text-muted-foreground">Choose how to get them. Nothing is written until you click.</p>
      <div className="flex flex-wrap gap-2" role="group" aria-label="How to get the missing details">
        <Button
          type="button"
          variant={choice === "ask" ? "default" : "outline"}
          aria-pressed={choice === "ask"}
          onClick={() => {
            setChoice("ask");
          }}
          data-testid="fogbugz-to-ado-choose-ask"
        >
          <MessageSquareShare aria-hidden="true" />
          Ask the reporter
        </Button>
        <Button
          type="button"
          variant={choice === "answer" ? "default" : "outline"}
          aria-pressed={choice === "answer"}
          onClick={() => {
            setChoice("answer");
          }}
          data-testid="fogbugz-to-ado-choose-answer"
        >
          <UserPen aria-hidden="true" />
          I know the answers
        </Button>
      </div>

      {choice === "ask" && (
        <div className="flex flex-col gap-3" data-testid="fogbugz-to-ado-ask">
          <p className="text-[13px] text-muted-foreground">
            These questions go to case #{caseId} as one note from you. Edit them first if you like; nothing is written to
            ADO.
          </p>
          <ol className="flex flex-col gap-2">
            {questions.map((question, index) => (
              <li key={question.slot} className="flex flex-col gap-1">
                <label className="text-xs font-medium text-muted-foreground" htmlFor={`fogbugz-to-ado-question-${question.slot}`}>
                  {FOGBUGZ_TO_ADO_SLOT_LABELS[question.slot]}
                </label>
                <textarea
                  id={`fogbugz-to-ado-question-${question.slot}`}
                  value={question.question}
                  maxLength={FOGBUGZ_TO_ADO_REPORTER_QUESTION_MAX}
                  onChange={(event) => {
                    const text = event.target.value;
                    setQuestions((current) => current.map((q, i) => (i === index ? { ...q, question: text } : q)));
                  }}
                  className={cn(fieldClass, "min-h-[56px]")}
                />
              </li>
            ))}
          </ol>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">The note, exactly as it will be posted</span>
            <pre
              className="max-h-[40vh] overflow-y-auto whitespace-pre-wrap rounded-md border border-white/[0.08] bg-white/[0.02] p-3 text-[13px]"
              data-testid="fogbugz-to-ado-questions-preview"
            >
              {preview}
            </pre>
          </div>
          {post.isError && (
            <Notice tone="error" testId="fogbugz-to-ado-post-error">
              <p>{errorText(post.error, `Could not post the questions to case #${String(caseId)}. Nothing was posted.`)}</p>
            </Notice>
          )}
          <div>
            <Button
              type="button"
              disabled={postable.length === 0 || post.isPending}
              onClick={postQuestions}
              data-testid="fogbugz-to-ado-post-questions"
            >
              {post.isPending ? "Posting…" : `Post to FogBugz case ${String(caseId)}`}
            </Button>
          </div>
        </div>
      )}

      {choice === "answer" && (
        <div className="flex flex-col gap-3" data-testid="fogbugz-to-ado-answer">
          <p className="text-[13px] text-muted-foreground">
            Answer each one. What you add goes into the Bug, and the note posted to case #{caseId} lists it under your
            name, so everyone can see it came from you.
          </p>
          {askSlots.map((slot) => {
            const prompt = prompts.find((question) => question.slot === slot)?.question ?? FOGBUGZ_TO_ADO_SLOT_LABELS[slot];
            return (
              <div key={slot} className="flex flex-col gap-1">
                <label className="text-[13px] font-medium" htmlFor={`fogbugz-to-ado-answer-${slot}`}>
                  {FOGBUGZ_TO_ADO_SLOT_LABELS[slot]}
                </label>
                <span className="text-xs text-muted-foreground">{prompt}</span>
                <textarea
                  id={`fogbugz-to-ado-answer-${slot}`}
                  value={answers[slot] ?? ""}
                  maxLength={FOGBUGZ_TO_ADO_SLOT_VALUE_MAX}
                  onChange={(event) => {
                    const text = event.target.value;
                    setAnswers((current) => ({ ...current, [slot]: text }));
                  }}
                  className={cn(fieldClass, "min-h-[64px]")}
                />
              </div>
            );
          })}
          {recheck.isError && (
            <Notice tone="error" testId="fogbugz-to-ado-recheck-error">
              <p>{errorText(recheck.error, "Could not check the Bug bar. Try again.")}</p>
            </Notice>
          )}
          <div>
            <Button
              type="button"
              disabled={recheck.isPending || askSlots.every((slot) => (answers[slot] ?? "").trim() === "")}
              onClick={checkAgain}
              data-testid="fogbugz-to-ado-check-again"
            >
              {recheck.isPending ? "Checking…" : "Check the Bug bar again"}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
